import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Intro — premium slow-motion zoom on the Victory logo.
 * · Background: rich crimson→black radial gradient with warm gold + silver drift
 *   (matches the site's crimson/ivory/gold palette — no plain black).
 * · Logo: perfectly centered, slow-motion zoom from tiny to nearly full-frame,
 *   with a glossy mirror-like sheen sweeping across it.
 * · Curtain wipe → site.
 */

const LOGO_RED_URL =
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_75990839-45d2-4b14-a4ad-e51df4156482/artifacts/6zl932yo_Victory_Logo_Red.png";

const Intro = () => {
    const [phase, setPhase] = useState(0); // 0 running, 1 curtain, 2 done

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("victory_intro_done")) {
            setPhase(2);
            return;
        }
        const reduced =
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) {
            sessionStorage.setItem("victory_intro_done", "1");
            setPhase(2);
            return;
        }
        const t1 = setTimeout(() => setPhase(1), 4400);
        const t2 = setTimeout(() => {
            sessionStorage.setItem("victory_intro_done", "1");
            setPhase(2);
        }, 5300);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    return (
        <AnimatePresence>
            {phase < 2 && (
                <motion.div
                    key="intro"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="fixed inset-0 z-[100] overflow-hidden"
                    style={{
                        background:
                            "radial-gradient(ellipse at 50% 50%, #3a0505 0%, #1a0303 45%, #050101 100%)",
                        perspective: 1400,
                    }}
                    data-testid="intro-loader"
                >
                    {/* Warm gold drift (top-left) — brand accent */}
                    <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute rounded-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.55, x: [0, 40, 0], y: [0, 20, 0] }}
                        transition={{
                            opacity: { duration: 1.4, ease: [0.22, 1, 0.36, 1] },
                            x: { duration: 14, repeat: Infinity, ease: "easeInOut" },
                            y: { duration: 12, repeat: Infinity, ease: "easeInOut" },
                        }}
                        style={{
                            width: "70vmin",
                            height: "70vmin",
                            top: "-15vmin",
                            left: "-15vmin",
                            background:
                                "radial-gradient(circle, rgba(201,163,90,0.65) 0%, rgba(201,163,90,0.22) 32%, rgba(0,0,0,0) 70%)",
                            filter: "blur(60px)",
                        }}
                    />

                    {/* Cool silver drift (bottom-right) */}
                    <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute rounded-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4, x: [0, -30, 0], y: [0, -20, 0] }}
                        transition={{
                            opacity: { duration: 1.4, ease: [0.22, 1, 0.36, 1] },
                            x: { duration: 16, repeat: Infinity, ease: "easeInOut" },
                            y: { duration: 13, repeat: Infinity, ease: "easeInOut" },
                        }}
                        style={{
                            width: "80vmin",
                            height: "80vmin",
                            bottom: "-20vmin",
                            right: "-20vmin",
                            background:
                                "radial-gradient(circle, rgba(210,215,225,0.55) 0%, rgba(180,190,205,0.18) 30%, rgba(0,0,0,0) 70%)",
                            filter: "blur(70px)",
                        }}
                    />

                    {/* Crimson glow behind the logo — grows with the zoom */}
                    <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                        initial={{ scale: 0.1, opacity: 0 }}
                        animate={{ scale: 1.4, opacity: 0.55 }}
                        transition={{
                            duration: 3.4,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.4,
                        }}
                        style={{
                            width: "80vmin",
                            height: "80vmin",
                            background:
                                "radial-gradient(circle, rgba(255,70,50,0.45) 0%, rgba(185,28,28,0.28) 30%, rgba(0,0,0,0) 65%)",
                            filter: "blur(50px)",
                        }}
                    />

                    {/* Grain overlay */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 opacity-25 mix-blend-overlay"
                        style={{
                            backgroundImage:
                                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                        }}
                    />

                    {/* Vignette */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(0,0,0,0.7) 100%)",
                        }}
                    />

                    {/* LOGO — center, slow-motion zoom, glossy mirror sheen */}
                    <motion.div
                        className="absolute left-1/2 top-1/2 flex items-center justify-center"
                        initial={{
                            scale: 0.04,
                            opacity: 0,
                            x: "-50%",
                            y: "-50%",
                        }}
                        animate={{
                            scale: 1.35,
                            opacity: 1,
                            x: "-50%",
                            y: "-50%",
                        }}
                        transition={{
                            duration: 3.8,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.3,
                        }}
                    >
                        <div
                            className="relative"
                            style={{
                                width: "min(62vmin, 720px)",
                                aspectRatio: "3 / 2",
                            }}
                        >
                            {/* Base red logo (bottom layer) */}
                            <img
                                src={LOGO_RED_URL}
                                alt="Victory"
                                className="absolute inset-0 h-full w-full object-contain"
                                style={{
                                    filter:
                                        "drop-shadow(0 20px 60px rgba(255,60,40,0.5)) drop-shadow(0 0 30px rgba(201,163,90,0.35))",
                                }}
                                draggable={false}
                            />
                            {/* Glossy mirror sheen — a moving diagonal white gradient
                                clipped to the logo shape via mask-image */}
                            <motion.div
                                aria-hidden="true"
                                className="absolute inset-0"
                                initial={{ backgroundPosition: "-200% 0%" }}
                                animate={{ backgroundPosition: "200% 0%" }}
                                transition={{
                                    duration: 3.6,
                                    ease: "easeInOut",
                                    delay: 1.2,
                                }}
                                style={{
                                    backgroundImage:
                                        "linear-gradient(115deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.55) 45%, rgba(255,240,220,0.9) 50%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0) 70%)",
                                    backgroundSize: "220% 100%",
                                    WebkitMaskImage: `url(${LOGO_RED_URL})`,
                                    maskImage: `url(${LOGO_RED_URL})`,
                                    WebkitMaskSize: "contain",
                                    maskSize: "contain",
                                    WebkitMaskRepeat: "no-repeat",
                                    maskRepeat: "no-repeat",
                                    WebkitMaskPosition: "center",
                                    maskPosition: "center",
                                    mixBlendMode: "screen",
                                }}
                            />
                            {/* Persistent soft highlight (top-left) for the mirror feel */}
                            <div
                                aria-hidden="true"
                                className="absolute inset-0"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(160deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0) 55%)",
                                    WebkitMaskImage: `url(${LOGO_RED_URL})`,
                                    maskImage: `url(${LOGO_RED_URL})`,
                                    WebkitMaskSize: "contain",
                                    maskSize: "contain",
                                    WebkitMaskRepeat: "no-repeat",
                                    maskRepeat: "no-repeat",
                                    WebkitMaskPosition: "center",
                                    maskPosition: "center",
                                    mixBlendMode: "screen",
                                    opacity: 0.9,
                                }}
                            />
                        </div>
                    </motion.div>

                    {/* Curtain wipe on exit */}
                    <motion.div
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 z-10"
                        initial={{ height: "0%" }}
                        animate={{ height: phase >= 1 ? "110%" : "0%" }}
                        transition={{
                            duration: 0.9,
                            ease: [0.65, 0, 0.35, 1],
                        }}
                        style={{
                            background:
                                "linear-gradient(180deg, #F1E6D0 0%, #F6EFE1 60%, #F6EFE1 100%)",
                        }}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Intro;
